import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import type { EtlDailyRollupDay } from '../types/operations.types';
import {
  formatRollupTooltip,
  ROLLUP_BAR_COLOURS,
  ROLLUP_STATUS_LABELS,
} from '../helpers/etl-health.helpers';

type Props = {
  days: EtlDailyRollupDay[];
  selectedDate: string | null;
  onSelectDate: (date: string | null) => void;
};

export function EtlHealthStripV2({ days, selectedDate, onSelectDate }: Props) {
  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4 text-[11px] font-medium tracking-[0.3px] text-[#5E6278]">
          {(['green', 'yellow', 'red', 'no_data'] as const).map((status) => (
            <span key={status} className="inline-flex items-center gap-1.5">
              <span
                className={cn('inline-block size-2.5 rounded-sm', ROLLUP_BAR_COLOURS[status].split(' ')[0])}
                aria-hidden="true"
              />
              {ROLLUP_STATUS_LABELS[status]}
            </span>
          ))}
        </div>

        <div
          className={portalDs.healthStrip}
          role="list"
          aria-label="30-day ETL health strip"
        >
          {days.map((day) => {
            const isSelected = selectedDate === day.date;
            return (
              <Tooltip key={day.date}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="listitem"
                    aria-label={formatRollupTooltip(day)}
                    aria-pressed={isSelected}
                    onClick={() => onSelectDate(isSelected ? null : day.date)}
                    className={cn(
                      'min-w-0 flex-1 rounded-sm transition-all focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(37,99,235,0.20)]',
                      ROLLUP_BAR_COLOURS[day.overall_status],
                      isSelected ? 'h-12 ring-2 ring-[#2563EB] ring-offset-1' : 'h-8',
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{formatRollupTooltip(day)}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className="flex justify-between text-[11px] font-medium tracking-[0.3px] text-[#9CA3AF]">
          <span>{days[0]?.date ?? ''}</span>
          <span>{days[days.length - 1]?.date ?? ''}</span>
        </div>
      </div>
    </TooltipProvider>
  );
}
